import React, { useCallback, useEffect, useRef } from "react";

/**
 * Usage
 * ```tsx
 * const MyComponent = () => {
 *     const elementRef = useIntersectionObserver((entry) => { ... do something ...}, options)
 *
 *     return <div ref={elementRef} />
 * }
 * ```
 * @param callback Callback gets fired everytime IntersectionObserver entry call is fired.
 * @param options Same as IntersectionObserverInit. NOTE: options are NOT reactive.
 */
export const useIntersectionObserver = <E extends Element>(
  callback: (
    entry: IntersectionObserverEntry,
    observer: IntersectionObserver
  ) => void,
  { threshold, rootMargin, root }: Readonly<IntersectionObserverInit> = {
    rootMargin: "0px",
  }
): React.RefCallback<E | undefined> => {
  const observerRef = useRef<IntersectionObserver | null>(null);

  // Always use the latest callback, without having to need to run useEffect again
  const callBackRef = useRef(callback);
  callBackRef.current = callback;

  useEffect(() => {
    return () => {
      observerRef.current?.disconnect();
    };
  }, []);

  return useCallback(
    (e: E | null | undefined) => {
      if (!e) {
        return;
      }

      const observer = new IntersectionObserver(
        (entries, observer) => {
          entries.forEach((entry) => {
            callBackRef.current(entry, observer);
          });
        },
        { rootMargin, root, threshold }
      );
      observerRef.current?.disconnect();
      observer.observe(e);
      observerRef.current = observer;
    },
    [root, rootMargin, threshold]
  );
};
